"use client";

import React, { useState, useEffect } from "react";

import * as Icon from "@phosphor-icons/react/dist/ssr";
import productData from "../../data/Product.json";
import { ProductType } from "../../type/ProductType";
import { useModalCartContext } from "../../context/ModalCartContext";
import { useCart, notifyCartStorageChanged } from "../../context/CartContext";
import { fetchProductImages, fetchProductText, productList } from "../../data/Productlist";
import { makeCartVariantKey } from "../../utils/cartVariantKey";
import { PriceDisplay } from "../Other/PriceDisplay";

const ModalCart = () => {
  const [activeTab, setActiveTab] = useState<string | undefined>("");
  const { isModalOpen, closeModalCart } = useModalCartContext();
  const { cartState, addToCart, removeFromCart, updateCart } = useCart();
  const [isCheckingOut, setIsCheckingOut] = useState(false);

  const handleAddToCart = (productItem: ProductType) => {
    if (!cartState.cartArray.find((item) => item.id === productItem.id)) {
      addToCart({ ...productItem });
      updateCart(productItem.id, productItem.quantityPurchase, "", "");
    } else {
      updateCart(productItem.id, productItem.quantityPurchase, "", "");
    }
  };



  let moneyForFreeship = 150;

  const [dataSourceProd, setDataSourceProd] = useState<any[]>([]);
  const [isOpenProduct, setisOpenProduct] = useState(true);
  const [isLoadingText, setIsLoadingText] = useState(true);
  const [isLoadingImages, setIsLoadingImages] = useState(true);

  const fetchData = async () => {
    try {
      // Fetch text data first
      const products = await fetchProductText();

      // Set text data first (without images)
      setDataSourceProd(products);
      setisOpenProduct(false);
      setIsLoadingText(false); // Text data loaded

      // Fetch images for the products after the text data is loaded
      const productsWithImages = await fetchProductImages(products);

      // Set the updated products with images
      setDataSourceProd(productsWithImages);

      setIsLoadingImages(false); // Images data loaded
    } catch (error) {
      console.error("Failed to fetch products: ", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const [cartDetails, setCartDetails] = useState<any[]>([]);
  const [colorImages, setColorImages] = useState<{[key: number | string]: string}>({});

  const fetchColorImage = async (imageid: number | string) => {
    if (!imageid || colorImages[imageid]) return; // Already loaded or no imageid
    
    // Use direct path instead of API call
    const imagePath = `/images/${imageid}`;
    setColorImages(prev => ({
      ...prev,
      [imageid]: imagePath
    }));
  };

  const updateCartDetails = async () => {
    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: any[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];

    // Build cart lines from cartItems so same product can appear
    // multiple times with different variations.
    const updatedCartDetails = cartItems
      .map((cartItem) => {
        const product = dataSourceProd.find(
          (p) => Number(p.id) === Number(cartItem.id)
        );
        if (!product) return null;

        const variantKey = makeCartVariantKey({
          productId: cartItem.id,
          colorCode: cartItem.colorCode,
          sizeCode: cartItem.sizeCode,
          pointureCode: cartItem.pointureCode,
        });

        return {
          ...product,
          variantKey,
          quantityPursh: cartItem.quantity ?? 0,
          sizePursh: cartItem.size ?? null,
          colorPursh: cartItem.color ?? null,
          colorCode: cartItem.colorCode ?? null,
          sizeCode: cartItem.sizeCode ?? null,
          pointureCode: cartItem.pointureCode ?? null,
          colorImageId: cartItem.colorImageId ?? null,
        };
      })
      .filter(Boolean) as any[];

    // Set the updated cart details state
    setCartDetails(updatedCartDetails);

    // Set color images directly from cart data (only for products with characteristics)
    for (const product of updatedCartDetails) {
      if (product.colorImageId && !product.colorImageId.includes('.') && !product.colorImageId.startsWith('/')) {
        // Only fetch if it's a numeric ID (not a filename like "59-2.jpg")
        fetchColorImage(product.colorImageId);
      }
    }
  };

  const handleAddToCartProd = (productMain: any) => {
    // Get existing cart items from local storage
    const cartItemsJSON = localStorage.getItem("cartItems");
    const cartItems: { id: string | number; quantity: number }[] = cartItemsJSON ? JSON.parse(cartItemsJSON) : [];
  
    // Create a new cart item
    const newCartItem = {
      id: productMain?.id, // Ensure productMain.id is defined
      quantity: 1, // Use the quantity from your state or prop
    };
  
    // Check if the new cart item's ID is defined
    if (!newCartItem.id) {
      console.error("Product ID is not defined.");
      return; // Exit if there's no product ID
    }
  
    // Check if the item already exists in the cart
    const existingItemIndex = cartItems.findIndex(item => item.id === newCartItem.id);
  
    if (existingItemIndex >= 0) {
      // If it exists, update the quantity
      cartItems[existingItemIndex].quantity += newCartItem.quantity;
    } else {
      // If it doesn't exist, add the new item to the cart
      cartItems.push(newCartItem);
    }
  
    // Save updated cart items back to local storage
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
    notifyCartStorageChanged();
  
    // Optionally, you can add a confirmation message or update the UI

    updateCartDetails()
  };

  useEffect(() => {
    // Initialize the cart details on component mount
    updateCartDetails();

    const intervalId = setInterval(updateCartDetails, 1000);

    // Clear the interval on component unmount
    return () => clearInterval(intervalId);
  }, [dataSourceProd]);

  let [totalCart, setTotalCart] = useState<number>(0);
  let [discountCart, setDiscountCart] = useState<number>(0);

  cartState.cartArray.map((item) => (totalCart += item.price * item.quantity));

  const calculateTotalPrice = () => {
    return cartDetails.reduce((total, product) => {
      return total + product.price * product.quantityPursh;
    }, 0);
  };

  useEffect(() => {
    // Update the total cart price whenever cartDetails changes
    const total = calculateTotalPrice();
    setTotalCart(total);
  }, [cartDetails]);

  const removeFromCartProd = (variantKey: string) => {
    const cartItemsJSON = localStorage.getItem("cartItems");
    if (cartItemsJSON) {
      const cartItems = JSON.parse(cartItemsJSON);
      const updatedCartItems = cartItems.filter((item: any) => {
        const itemVariantKey = makeCartVariantKey({
          productId: item.id,
          colorCode: item.colorCode,
          sizeCode: item.sizeCode,
          pointureCode: item.pointureCode,
        });
        return itemVariantKey !== variantKey;
      });
      localStorage.setItem("cartItems", JSON.stringify(updatedCartItems));
      notifyCartStorageChanged();
    }

    updateCartDetails();
  };

  const handleCheckout = async (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (cartDetails.length === 0) {
      e.preventDefault();
      return;
    }
    
    e.preventDefault();
    setIsCheckingOut(true);

    // Show loading state for 3 seconds
    await new Promise(resolve => setTimeout(resolve, 3000));

    // Close modal and navigate to checkout
    closeModalCart();
    window.location.href = "/checkout";
  };

  return (
    <>
      <div className={`modal-cart-block`} onClick={closeModalCart}>
        <div
          className={`modal-cart-main flex ${isModalOpen ? "open" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <div className="left w-1/2 border-r border-line py-6 max-md:hidden">
            <div className="heading5 px-6 pb-3">
              Vous aimerez peut-être aussi
            </div>
            <div className="list px-6">
              {dataSourceProd.slice(0, 4).map((product) => (
                <div
                  key={product.id}
                  className="item py-5 flex items-center justify-between gap-3 border-b border-line"
                >
                  <div className="infor flex items-center gap-5">
                    <div className="bg-img">
                      <img
                        src={
                          product.colorImageId 
                            ? (product.colorImageId.startsWith('/') ? product.colorImageId : `/images/${product.colorImageId}`)
                            : `/images/${product.id}.jpg`
                        }
                        width={300}
                        height={300}
                        alt={product.name}
                        onError={(e) => {
                          e.currentTarget.src = "/default.png"; 
                        }}
                        className="w-[100px] aspect-square flex-shrink-0 rounded-lg"
                      />
                    </div>
                    <div className="">
                      <div className="name text-button">{product.name}</div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="product-price text-title">
                          <PriceDisplay amount={product.price} />
                        </div>
                        <div className="product-origin-price text-title text-secondary2">
                          <del>
                            <PriceDisplay amount={product.originPrice} size="sm" />
                          </del>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    className="text-xl bg-white w-10 h-10 rounded-xl border border-black flex items-center justify-center duration-300 cursor-pointer hover:bg-black hover:text-white"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddToCartProd(product);
                    }}
                  >
                    <Icon.Handbag />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="right cart-block md:w-1/2 w-full py-6 relative overflow-hidden">
            <div className="heading px-6 pb-3 flex items-center justify-between relative">
              <div className="heading5">Panier</div>
              <div
                className="close-btn absolute right-6 top-0 w-6 h-6 rounded-full bg-surface flex items-center justify-center duration-300 cursor-pointer hover:bg-black hover:text-white"
                onClick={closeModalCart}
              >
                <Icon.X size={14} />
              </div>
            </div>
            <div className="heading banner mt-3 px-6">
              <div className="text">
                Achetez{" "}
                <span className="text-button more-price">
                  {" "}
                  <PriceDisplay
                    amount={
                      moneyForFreeship - totalCart > 0
                        ? moneyForFreeship - totalCart
                        : 0
                    }
                    size="sm"
                  />{" "}
                </span>
                <span>de plus pour obtenir </span>
                <span className="text-button">la livraison gratuite</span>
              </div>
              <div className="tow-bar-block mt-3">
                <div
                  className="progress-line"
                  style={{
                    width:
                      totalCart <= moneyForFreeship
                        ? `${(totalCart / moneyForFreeship) * 100}%`
                        : `100%`,
                  }}
                ></div>
              </div>
            </div>
            <div className="list-product px-6">
              {cartDetails.map((product) => (
                <div
                  key={product.variantKey}
                  className="item py-5 flex items-center justify-between gap-3 border-b border-line"
                >
                  <div className="infor flex items-center gap-3 w-full">
                    <div className="bg-img w-[100px] aspect-square flex-shrink-0 rounded-lg overflow-hidden">
                      <img
                        src={
                          product.colorImageId 
                            ? (product.colorImageId.startsWith('/') ? product.colorImageId : `/images/${product.colorImageId}`)
                            : `/images/${product.id}.jpg`
                        }
                        width={300}
                        height={300}
                        onError={(e) => {
                          e.currentTarget.src = "/default.png"; 
                        }}
                        alt={product.name}
                        className="w-full h-full"
                      />
                    </div>
                    <div className="w-full">
                      <div className="flex items-center justify-between w-full">
                        <div className="name text-button">{product.name}</div>
                        <div
                          className="remove-cart-btn caption1 font-semibold text-red underline cursor-pointer"
                          onClick={() => removeFromCartProd(product.variantKey)}
                        >
                          Supprimer
                        </div>
                      </div>
                      {(product.colorPursh || product.sizePursh) && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {product.sizePursh && (
                            <span className="bg-blue-100 text-blue-600 text-xs px-2 py-1 rounded-full">
                              T: {product.sizePursh}
                            </span>
                          )}
                          {product.colorPursh && (
                            <span className="bg-purple-100 text-purple-600 text-xs px-2 py-1 rounded-full">
                              C: {product.colorPursh}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="flex items-center justify-between gap-2 mt-3 w-full">
                        <div className="product-price text-title">
                          <PriceDisplay amount={product.price} />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="footer-modal bg-white absolute bottom-0 left-0 w-full">
              <div className="flex items-center justify-between pt-6 px-6">
                <div className="heading5">Sous-total</div>
                <div className="heading5">
                  <PriceDisplay amount={totalCart} size="lg" />
                </div>
              </div>
              <div className="block-button text-center p-6">
                <div className="flex items-center gap-4">
                  <a
                    href={cartDetails.length === 0 ? "#" : "/cart"}
                    className={`button-main basis-1/2 bg-white border border-black text-black text-center uppercase ${
                      cartDetails.length === 0 
                        ? 'opacity-50 cursor-not-allowed bg-gray-200 border-gray-300 text-gray-500' 
                        : ''
                    }`}
                    onClick={(e) => {
                      if (cartDetails.length === 0) {
                        e.preventDefault();
                      } else {
                        closeModalCart();
                      }
                    }}
                  >
                    {cartDetails.length === 0 ? 'Panier vide' : 'Voir le panier'}
                  </a>
                  <a
                    href={cartDetails.length === 0 ? "#" : "/checkout"}
                    className={`button-main bg-pink basis-1/2 text-center uppercase flex items-center justify-center gap-2 ${
                      cartDetails.length === 0 
                        ? 'opacity-50 cursor-not-allowed bg-gray-400 hover:bg-gray-400' 
                        : ''
                    } ${isCheckingOut ? 'opacity-75 cursor-not-allowed' : ''}`}
                    onClick={handleCheckout}
                  >
                    {isCheckingOut ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Préparation...
                      </>
                    ) : (
                      cartDetails.length === 0 ? 'Panier vide' : 'Passer à la caisse'
                    )}
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    closeModalCart();
                  }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-xl border-2 border-pink bg-white px-4 py-3.5 text-sm font-semibold uppercase tracking-wide text-pink shadow-sm transition-all duration-200 hover:bg-pink hover:text-white hover:shadow-md active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-pink focus-visible:ring-offset-2"
                >
                  <Icon.Storefront size={22} weight="duotone" className="shrink-0" />
                  <span>Ou continuer vos achats</span>
                  <Icon.ArrowRight size={18} weight="bold" className="shrink-0 opacity-90" />
                </button>
              </div>
              <div
                className={`tab-item note-block ${
                  activeTab === "note" ? "active" : ""
                }`}
              >
                <div className="px-6 py-4 border-b border-line">
                  <div className="item flex items-center gap-3 cursor-pointer">
                    <Icon.NotePencil className="text-xl" />
                    <div className="caption1">Note</div>
                  </div>
                </div>
                <div className="form pt-4 px-6">
                  <textarea
                    name="form-note"
                    id="form-note"
                    rows={4}
                    placeholder="Ajoutez des instructions spéciales pour votre commande..."
                    className="caption1 py-3 px-4 bg-surface border-line rounded-md w-full"
                  ></textarea>
                </div>
                <div className="block-button text-center pt-4 px-6 pb-6">
                  <div
                    className="button-main w-full text-center"
                    onClick={() => setActiveTab("")}
                  >
                    Enregistrer
                  </div>
                  <div
                    onClick={() => setActiveTab("")}
                    className="text-button-uppercase mt-4 text-center has-line-before cursor-pointer inline-block"
                  >
                    Annuler
                  </div>
                </div>
              </div>
              <div
                className={`tab-item note-block ${
                  activeTab === "shipping" ? "active" : ""
                }`}
              >
                <div className="px-6 py-4 border-b border-line">
                  <div className="item flex items-center gap-3 cursor-pointer">
                    <Icon.Truck className="text-xl" />
                    <div className="caption1">
                      Estimez les frais d'expédition
                    </div>
                  </div>
                </div>
                <div className="form pt-4 px-6">
                  <div className="">
                    <label
                      htmlFor="select-country"
                      className="caption1 text-secondary"
                    >
                      Pays/région
                    </label>
                    <div className="select-block relative mt-2">
                      <select
                        id="select-country"
                        name="select-country"
                        className="w-full py-3 pl-5 rounded-xl bg-white border border-line"
                        defaultValue={"Pays/région"}
                      >
                        <option value="Pays/région" disabled>
                          Pays/région
                        </option>
                        <option value="France">France</option>
                        <option value="Espagne">Espagne</option>
                        <option value="Royaume-Uni">Royaume-Uni</option>
                        <option value="États-Unis">États-Unis</option>
                      </select>
                      <Icon.CaretDown
                        size={12}
                        className="absolute top-1/2 -translate-y-1/2 md:right-5 right-2"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label
                      htmlFor="select-state"
                      className="caption1 text-secondary"
                    >
                      État
                    </label>
                    <div className="select-block relative mt-2">
                      <select
                        id="select-state"
                        name="select-state"
                        className="w-full py-3 pl-5 rounded-xl bg-white border border-line"
                        defaultValue={"État"}
                      >
                        <option value="État" disabled>
                          État
                        </option>
                        <option value="Paris">Paris</option>
                        <option value="Madrid">Madrid</option>
                        <option value="Londres">Londres</option>
                        <option value="New York">New York</option>
                      </select>
                      <Icon.CaretDown
                        size={12}
                        className="absolute top-1/2 -translate-y-1/2 md:right-5 right-2"
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <label
                      htmlFor="select-code"
                      className="caption1 text-secondary"
                    >
                      Code postal
                    </label>
                    <input
                      className="border-line px-5 py-3 w-full rounded-xl mt-3"
                      id="select-code"
                      type="text"
                      placeholder="Code postal"
                    />
                  </div>
                </div>
                <div className="block-button text-center pt-4 px-6 pb-6">
                  <div
                    className="button-main w-full text-center"
                    onClick={() => setActiveTab("")}
                  >
                    Calculateur
                  </div>
                  <div
                    onClick={() => setActiveTab("")}
                    className="text-button-uppercase mt-4 text-center has-line-before cursor-pointer inline-block"
                  >
                    Annuler
                  </div>
                </div>
              </div>
              <div
                className={`tab-item note-block ${
                  activeTab === "coupon" ? "active" : ""
                }`}
              >
                <div className="px-6 py-4 border-b border-line">
                  <div className="item flex items-center gap-3 cursor-pointer">
                    <Icon.Tag className="text-xl" />
                    <div className="caption1">Ajouter un code de coupon</div>
                  </div>
                </div>
                <div className="form pt-4 px-6">
                  <div className="">
                    <label
                      htmlFor="select-discount"
                      className="caption1 text-secondary"
                    >
                      Entrez le code
                    </label>
                    <input
                      className="border-line px-5 py-3 w-full rounded-xl mt-3"
                      id="select-discount"
                      type="text"
                      placeholder="Code de réduction"
                    />
                  </div>
                </div>
                <div className="block-button text-center pt-4 px-6 pb-6">
                  <div
                    className="button-main w-full text-center"
                    onClick={() => setActiveTab("")}
                  >
                    Appliquer
                  </div>
                  <div
                    onClick={() => setActiveTab("")}
                    className="text-button-uppercase mt-4 text-center has-line-before cursor-pointer inline-block"
                  >
                    Annuler
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ModalCart;
